import React, { useState, FormEvent, ComponentType, useRef, useEffect, useCallback } from 'react';

import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { Text, formatDate, Avatar, Button, Modal } from '@morressier/ts';

import useMediaQuery from '@material-ui/core/useMediaQuery';
import { convertFromRaw, ContentState } from 'draft-js';
import { FaCheck } from 'react-icons/fa';

import { updateDiscussion, approveDiscussion } from 'api/discussions';
import { approveRtmpDiscussion, updateRtmpDiscussions } from 'api/livestream-rtmp';
import { BREAKPOINT_SMALL } from 'constants/breakpoints';
import { useRtmpContext } from 'containers/SingleSession/Rtmp/RtmpProvider';
import { simpleToast } from 'helpers/simpleToast';
import { useAuthContext } from 'store';
import { toLocaleString } from 'utils/dates';

import { MentionsEditorProps, Mentions, MentionsEvent, getReplyTag } from './MentionsEditor';
import * as Styles from './style';

const MentionEditor: ComponentType<MentionsEditorProps> = dynamic(
  () => import('components/Comment/MentionsEditor'),
  { ssr: false },
);

export interface CommentBoxProps {
  belongsToAuthUser?: boolean;
  canApprove?: boolean;
  discussion: MorressierDiscussion;
  hasReply?: boolean;
  isReply?: boolean;
  replyTo?: {
    threadId: string;
  } & Mentions;
  handleReply?: (
    parent_id: string,
    message: string,
    rawMessage: string,
    mentionedUsers: string[],
  ) => Promise<boolean>;
  handleDelete?: (id: string) => Promise<boolean>;
  authorChip?: string;
  mentions?: Mentions[];
  isApproved: boolean;
  setIsApproved: React.Dispatch<React.SetStateAction<boolean>>;
  canUserPostComment: boolean;
}

export const CommentBox: React.FC<CommentBoxProps> = props => {
  const {
    belongsToAuthUser,
    canApprove,
    discussion,
    children,
    hasReply,
    handleReply,
    handleDelete,
    isReply,
    replyTo,
    authorChip,
    mentions,
    isApproved,
    setIsApproved,
    canUserPostComment,
  } = props;

  const { id, content, created_at, edited_at, author } = discussion;
  const { isRtmpStream } = useRtmpContext();

  const isMobile = useMediaQuery(`(max-width:${BREAKPOINT_SMALL}px)`);

  const [showReplyInput, setShowReplyInput] = useState(false);
  const [showEditInput, setShowEditInput] = useState(false);
  const [updatingComment, setUpdatingComment] = useState(false);
  const [approvingComment, setApprovingComment] = useState(false);
  const [replyingComment, setReplyingComment] = useState(false);
  const [isEdited, setIsEdited] = useState(!!edited_at);

  const [mentionedUsersUpdate, setMentionedUsersUpdate] = useState<string[]>([]);
  const [mentionedUsersReply, setMentionedUsersReply] = useState<string[]>([]);

  const { state: authState } = useAuthContext();
  const isAuthenticated = authState?.authInfo?.access_token;

  const getDefaultMessage = useCallback(
    (contentString: string) => {
      try {
        return convertFromRaw(JSON.parse(contentString));
      } catch {
        return ContentState.createFromText(content);
      }
    },
    [content],
  );

  const [defaultMessage, setdefaultMessage] = useState(() => getDefaultMessage(content));

  const [replyMessage, setReplyMessage] = useState(() =>
    JSON.stringify(ContentState.createFromText(getReplyTag(replyTo?.name))),
  );
  const [rawReplyMessage, setRawReplyMessage] = useState('');
  const [replyTag] = useState(() => replyTo || undefined);

  const [updateMessage, setUpdateMessage] = useState('');
  const [rawUpdateMessage, setRawUpdateMessage] = useState('');

  const handleUpdate = async (e: FormEvent) => {
    e.preventDefault();

    if (updatingComment) return;

    try {
      setUpdatingComment(true);
      let response: MorressierDiscussion;

      if (isRtmpStream) {
        response = await updateRtmpDiscussions(
          discussion.session,
          {
            content: updateMessage,
            raw_content: rawUpdateMessage,
            mentions: mentionedUsersUpdate,
          },
          id,
        );
      } else {
        response = await updateDiscussion(
          {
            content: updateMessage,
            raw_content: rawUpdateMessage,
            mentions: mentionedUsersUpdate,
          },
          id,
        );
      }

      setUpdatingComment(false);
      setShowEditInput(false);
      setdefaultMessage(getDefaultMessage(response.content));
      setIsEdited(!!response.edited_at);
    } catch (error) {
      setUpdatingComment(false);
      simpleToast("Sorry, we couldn't update your comment.");
    }
  };

  const handleApprove = useCallback(async () => {
    if (approvingComment) return;

    try {
      setApprovingComment(true);
      if (isRtmpStream) {
        await approveRtmpDiscussion(discussion.session, id);
      } else {
        await approveDiscussion(id);
      }

      setApprovingComment(false);
      setIsApproved(true);
      simpleToast("The message has been approved, now it's visible for everyone.");
    } catch (error) {
      simpleToast("Sorry, we couldn't approve this comment.");
    }
  }, [approvingComment, id, setIsApproved, discussion.session, isRtmpStream]);

  const postReply = async (e: FormEvent) => {
    e.preventDefault();
    if (replyingComment) return;

    if (handleReply) {
      setReplyingComment(true);

      const success = await handleReply(
        isReply && replyTo?.threadId ? replyTo.threadId : id,
        replyMessage?.trim(),
        rawReplyMessage.trim(),
        mentionedUsersReply,
      );

      setReplyingComment(false);

      if (success) {
        setShowReplyInput(false);
        setReplyMessage('');
      }
    }
  };

  const [showDeleteModal, setshowDeleteModal] = useState(false);
  const [isDeletingComment, setisDeletingComment] = useState(false);

  const postDelete = async () => {
    if (isDeletingComment || !handleDelete) return;

    setisDeletingComment(true);

    const success = await handleDelete(id);

    if (success) {
      setisDeletingComment(false);
      setshowDeleteModal(false);
    }
  };

  const router = useRouter();
  const ref = useRef<React.RefObject<HTMLDivElement> | HTMLDivElement>(null);

  const [element, setElement] = useState<HTMLDivElement>();

  useEffect(() => {
    setdefaultMessage(getDefaultMessage(discussion.content));
  }, [discussion.content, getDefaultMessage]);

  useEffect(() => {
    setIsApproved(!!discussion.approved_at);
  }, [discussion.approved_at, setIsApproved]);

  useEffect(() => {
    setElement(ref.current as HTMLDivElement);
  }, []);

  useEffect(() => {
    const commentId = router.query['comment-id'];
    const autoApprove = router.query['auto-approve'] === 'true';

    if (commentId === discussion.id && element) {
      if (element.classList.contains('highlight')) {
        element.classList.remove('highlight');
      }

      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      element.classList.add('highlight');

      if (autoApprove && canApprove && !isApproved) {
        handleApprove();
      }
    }
  }, [element, canApprove, discussion.id, router.query, handleApprove, isApproved]);

  const onUpdateChange = (e: MentionsEvent) => {
    if (e.rawString === '') {
      return setUpdateMessage(e.rawString);
    }

    setMentionedUsersUpdate(e.mentions);
    setUpdateMessage(e.stringifiedEditorContent);
    return setRawUpdateMessage(e.rawString);
  };

  const onReplyChange = (e: MentionsEvent) => {
    if (e.rawString === '') {
      return setReplyMessage(e.rawString);
    }

    setMentionedUsersReply(e.mentions);
    setReplyMessage(e.stringifiedEditorContent);
    return setRawReplyMessage(e.rawString);
  };

  return (
    <>
      {showDeleteModal && (
        <Styles.BackDrop>
          <Modal
            copies={{
              title: (
                <Text style={{ fontSize: '23px' }} fontWeight="semiBold">
                  Delete Question?
                </Text>
              ),
            }}
            body={<Text>Are you sure you want to delete this question?</Text>}
            footer={
              <Styles.FlexRow justifyContent="flex-end" style={{ width: '100%' }}>
                <Button
                  style={{ marginRight: '1rem' }}
                  onClick={() => {
                    setshowDeleteModal(false);
                  }}
                >
                  Cancel
                </Button>
                <Button color="danger" loading={isDeletingComment} onClick={postDelete}>
                  Delete
                </Button>
              </Styles.FlexRow>
            }
            close={() => setshowDeleteModal(false)}
            style={{ minWidth: '50%' }}
          />
        </Styles.BackDrop>
      )}
      <Styles.CommentWrapper
        isEditing={showEditInput}
        isApproved={isApproved}
        isReply={isReply}
        ref={ref as React.RefObject<HTMLDivElement>}
      >
        <Styles.FlexColumn style={{ width: '100%' }}>
          {canApprove && !isApproved && (
            <Styles.FlexRow justifyContent="flex-end">
              <Button
                style={{ padding: '0 1rem' }}
                onClick={handleApprove}
                small
                loading={approvingComment}
              >
                <FaCheck size="0.8rem" style={{ marginRight: '0.5rem' }} /> Approve visibility
              </Button>
            </Styles.FlexRow>
          )}

          <Styles.FlexRow>
            <Avatar
              customSize="2.5rem"
              customFontSize="1.1rem"
              fullName={author?.full_name || ''}
              src={author?.picture_url}
              style={{ margin: '0 0.5rem 0 0' }}
            />
            <Styles.FlexColumn style={{ width: '100%' }}>
              <Styles.FlexRow
                makeColumn={isMobile}
                style={{ alignItems: isMobile ? 'unset' : 'center' }}
              >
                <Styles.FlexRow style={{ alignItems: 'center' }}>
                  <Text as="span" fontWeight="bold">
                    {author?.full_name}
                  </Text>
                  {authorChip && (
                    <div style={{ marginLeft: '0.25rem' }}>
                      <Styles.PresenterChip>{authorChip}</Styles.PresenterChip>
                    </div>
                  )}
                </Styles.FlexRow>
                <Text
                  color="grey"
                  size="caption_new"
                  as="span"
                  style={{ marginLeft: isMobile ? '' : '0.5rem' }}
                >
                  {created_at && `${formatDate(created_at)} at ${toLocaleString(created_at)}`}
                </Text>
                {!isApproved && !isReply && (
                  <>
                    <Text
                      color="grey"
                      size="caption_new"
                      style={{ marginLeft: isMobile ? '' : '0.5rem' }}
                    >
                      · Waiting for approval
                    </Text>
                  </>
                )}
                {isEdited && (
                  <Text
                    color="grey"
                    size="caption_new"
                    style={{ marginLeft: isMobile ? '' : '0.5rem' }}
                  >
                    · Edited
                  </Text>
                )}
              </Styles.FlexRow>

              {!showEditInput ? (
                <>
                  <MentionEditor defaultValue={defaultMessage as ContentState} readOnly noBorder />

                  <Styles.FlexRow>
                    {isApproved && (
                      <Styles.SmallButton
                        onClick={() => setShowReplyInput(true)}
                        disabled={!isAuthenticated || canUserPostComment === false}
                      >
                        Reply
                      </Styles.SmallButton>
                    )}
                    {belongsToAuthUser && (
                      <>
                        <Styles.SmallButton onClick={() => setShowEditInput(true)}>
                          Edit
                        </Styles.SmallButton>
                        <Styles.SmallButton onClick={() => setshowDeleteModal(true)}>
                          Delete
                        </Styles.SmallButton>
                      </>
                    )}
                  </Styles.FlexRow>
                </>
              ) : (
                <Styles.FlexColumn as="form" onSubmit={handleUpdate}>
                  <MentionEditor
                    defaultValue={defaultMessage as ContentState}
                    mentions={mentions}
                    placeholder="Edit or type a new comment..."
                    rows={2}
                    style={{ backgroundColor: '#fff' }}
                    onChange={onUpdateChange}
                  />

                  <Styles.FlexRow justifyContent="flex-end">
                    <Button
                      onClick={() => {
                        setShowEditInput(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      type="submit"
                      loading={updatingComment}
                      style={{ marginLeft: '0.5rem' }}
                      disabled={updateMessage.trim().length <= 0}
                    >
                      Save edit
                    </Button>
                  </Styles.FlexRow>
                </Styles.FlexColumn>
              )}

              {hasReply && children}

              {showReplyInput && (
                <Styles.FlexColumn as="form" onSubmit={postReply}>
                  <MentionEditor
                    mentions={mentions}
                    initialMention={replyTag}
                    placeholder="Reply to this comment..."
                    rows={2}
                    style={{ backgroundColor: '#fff', marginTop: '2rem' }}
                    onChange={onReplyChange}
                    scrollIntoView
                  />
                  <Styles.FlexRow justifyContent="flex-end">
                    <Button onClick={() => setShowReplyInput(false)}>Cancel</Button>
                    <Button
                      type="submit"
                      color="primary"
                      loading={replyingComment}
                      style={{ marginLeft: '0.5rem' }}
                      disabled={replyMessage.trim().length <= 0}
                    >
                      Comment
                    </Button>
                  </Styles.FlexRow>
                </Styles.FlexColumn>
              )}
            </Styles.FlexColumn>
          </Styles.FlexRow>
        </Styles.FlexColumn>
      </Styles.CommentWrapper>
    </>
  );
};
